import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import AddIcon from '@material-ui/icons/Add';
import RateReviewIcon from '@material-ui/icons/RateReview';
import ReviewRecord from './ReviewRecord';
import { createReviewerComment } from '../../api/activities';
import { getReviewerComments } from '../../api/projectDocuments';
import { InspectionStatuses } from '../../constants';

export type RecordType = {
  comment: string;
  reference: string;
  status: any;
  index: number;
  id: number;
};

const ReviewerCommentForm1 = ({ reviewId, values }) => {
  const [records, setRecords] = useState<RecordType[]>([]);
  const [showSave, setShowSave] = useState<boolean>(false);
  const [ids, setIds] = useState<number[]>([]);
  const [statuses] = useState([
    { label: InspectionStatuses.REJECTED, value: 0 },
    { label: InspectionStatuses.APPROVED, value: 1 },
    { label: InspectionStatuses.NOT_APPLICABLE, value: 2 },
  ]);

  useEffect(() => {
    const load = async () => {
      await getReviewerComments(reviewId);
      const response = await getReviewerComments(reviewId);
      const _records = response.data.map((d, ndx) => {
        const status = statuses.find(s => s.label === d.status);
        return {
          comment: d.reviewerComment,
          reference: d.reference,
          status,
          index: ndx + 1,
          id: d.id,
        };
      });
      setRecords(_records);
    };
    load();
  }, []);

  const saveAll = async () => {
    const getStatus = stt => {
      if (typeof stt === 'object' && stt !== null && 'value' in stt) {
        return stt;
      }
      if (Number.isNaN(Number(stt))) {
        return stt;
      }
      const status = statuses.find(s => s.value === Number(stt));
      return status;
    };
    const data = {
      reviewId,
      reviewerCommentForm: records.map((r, ndx) => {
        const getId = (index, r_id) => {
          if (ids.length === 0) return r_id;
          if (index < ids.length) return ids[index];
          return 0;
        };
        return {
          id: getId(ndx, r.id),
          reference: r.reference,
          reviewer_comment: r.comment,
          status: getStatus(r.status),
        };
      }),
    };
    const result = await createReviewerComment(data);
    const _data: any = result.data || { updatedCommentIds: [] };
    setIds(_data.updatedCommentIds);
    setShowSave(true);
    setTimeout(() => {
      setShowSave(false);
    }, 1000);
  };

  const onChange = (e, index) => {
    const { name, value } = e.target;
    const _ndx = index - 1;
    const _records = records.map((r, ndx) => {
      if (ndx === _ndx) {
        return { ...r, [name]: value };
      }
      return r;
    });
    setRecords(_records);
  };

  const onCompoundChange = (name1, value1, name2, value2, index) => {
    const _ndx = index - 1;
    const _records = records.map((r, ndx) => {
      if (ndx === _ndx) {
        return { ...r, [name1]: value1, [name2]: value2 };
      }
      return r;
    });
    setRecords(_records);
  };

  const onAdd = () => {
    const status = statuses.find(s => s.value === 0);
    const _rec: RecordType = {
      comment: '',
      reference: '',
      status,
      index: records.length + 1,
      id: 0,
    };
    setRecords(prev => [...prev, _rec]);
  };

  return (
    <Card>
      <Header>
        <TitleGroup>
          <RateReviewIcon style={{ fontSize: 22, color: 'var(--primary-600)' }} />
          <Title>Review Comments</Title>
          <CountBadge>{records.length}</CountBadge>
        </TitleGroup>
      </Header>

      {records.length === 0 && (
        <EmptyState>
          <EmptyTitle>No review comments yet.</EmptyTitle>
          <EmptyHint>Click the button below to add your first reference and comment.</EmptyHint>
        </EmptyState>
      )}

      <List>
        {records.map(r => (
          <ReviewRecord
            key={r.id || r.index}
            showSave={showSave}
            record={r}
            onChange={onChange}
            onCompoundChange={onCompoundChange}
            statuses={statuses}
            initialValues={values}
            saveAll={saveAll}
          />
        ))}
      </List>

      <AddButton type="button" onClick={onAdd}>
        <AddIcon style={{ fontSize: 18 }} />
        <span>Add comment</span>
      </AddButton>
    </Card>
  );
};

export default ReviewerCommentForm1;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  font-family: ${FONT_STACK};
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TitleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  letter-spacing: -0.01em;

  @media only screen and (max-width: 540px) {
    font-size: 1rem;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.75rem;
  font-weight: 700;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
`;

const EmptyTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 4px;
`;

const EmptyHint = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const AddButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px dashed var(--primary-600);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--primary-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-style 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-style: solid;
    border-color: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    color: currentColor;
  }
`;
